/* Phase 248 — Shared "My Task Detail" page used by BOTH the in-house team
 * portal and the supplier portal. Provides:
 *   • Task title, description, status, dates
 *   • Project breadcrumb with "Get Directions" inline button
 *   • Two upload zones: Before photos / After photos (each with gallery)
 *   • Documents section (PDFs + images)
 *   • Notes section (textarea + history)
 *   • "Mark complete" — requires ≥1 After photo
 *
 * mode="in_house" (default) -> /api/in-house-portal/...
 * mode="vendor"             -> /api/supplier-portal/...
 */

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, MapPin, NavigationArrow, Camera, FileText, ChatCircle, CheckCircle,
  Image as ImageIcon, Clock, Plus, Spinner, Eye,
} from "@phosphor-icons/react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Skeleton } from "../components/ui/skeleton";
import { fmtDate } from "../lib/dateUtil";

function getDirectionsUrl(address) {
  if (!address) return null;
  const q = encodeURIComponent(address);
  const ua = (typeof navigator !== "undefined" ? navigator.userAgent : "") || "";
  if (/iPhone|iPad|iPod|Macintosh/i.test(ua)) return `https://maps.apple.com/?daddr=${q}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${q}`;
}

const STATUS_STYLES = {
  not_started: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  assigned: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  in_progress: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  completed_by_vendor: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  approved: "bg-emerald-200 text-emerald-900 dark:bg-emerald-900/50 dark:text-emerald-200",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

export default function MyTaskDetailPage({ mode = "in_house" }) {
  const { taskId, projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  const apiBase = mode === "vendor" ? "/supplier-portal" : "/in-house-portal";
  // For supplier portal, photos endpoint is /photos-v2 (multipart). In-house uses /photos.
  const photosEndpoint = mode === "vendor"
    ? `${apiBase}/tasks/${taskId}/photos-v2`
    : `${apiBase}/tasks/${taskId}/photos`;
  const docsEndpoint = `${apiBase}/tasks/${taskId}/documents`;
  const noteEndpoint = `${apiBase}/tasks/${taskId}/note`;
  const completeEndpoint = `${apiBase}/tasks/${taskId}/complete`;
  const projectBackTo = mode === "vendor"
    ? `/supplier-portal/projects/${projectId || data?.project?.id || ""}`
    : `/in-house/projects/${projectId || data?.project?.id || ""}`;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`${apiBase}/tasks/${taskId}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load task");
    } finally { setLoading(false); }
  }, [apiBase, taskId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const task = data?.task;
  const project = data?.project;
  const files = task?.files || [];
  const beforePhotos = files.filter(f => f.kind === "photo" && f.category === "before");
  const afterPhotos = files.filter(f => f.kind === "photo" && f.category === "after");
  const docs = files.filter(f => f.kind === "document");
  const notes = task?.notes || [];

  const fullAddress = useMemo(() => {
    if (!project) return "";
    return [project.property_address, project.property_postcode].filter(Boolean).join(", ");
  }, [project]);
  const directionsUrl = getDirectionsUrl(fullAddress || project?.property_address);

  const isClosed = ["approved", "completed_by_vendor"].includes(task?.status);

  const handleComplete = async () => {
    if (!afterPhotos.length) {
      toast.error("Add at least one 'After' photo before marking complete.");
      return;
    }
    if (!window.confirm("Mark this task as complete?")) return;
    setCompleting(true);
    try {
      await API.post(completeEndpoint);
      toast.success("Task marked complete — admin will review");
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark complete");
    } finally { setCompleting(false); }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto p-4 space-y-4 pwa-page">
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (!task) {
    return <div className="max-w-3xl mx-auto p-6 text-center text-sm text-muted-foreground">Task not found.</div>;
  }

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6 pwa-page" data-testid="my-task-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        data-testid="my-task-back-btn"
      >
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div className="rounded-xl border border-border bg-card p-5 mb-5 shadow-sm">
        <div className="flex items-start gap-2 mb-2 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] flex-1 min-w-0" data-testid="my-task-title">
            {task.title}
          </h1>
          <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded shrink-0 ${STATUS_STYLES[task.status] || STATUS_STYLES.not_started}`}>
            {(task.status || "new").replace(/_/g, " ")}
          </span>
        </div>

        {project && (
          <Link
            to={projectBackTo}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            data-testid="my-task-project-link"
          >
            in {project.name}
          </Link>
        )}

        {task.description && (
          <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap" data-testid="my-task-description">
            {task.description}
          </p>
        )}

        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-muted-foreground" data-testid="my-task-schedule">
          {task.start_date && (
            <span className="inline-flex items-center gap-1">
              <Clock size={12}/> Start {fmtDate(task.start_date)}
              {task.start_time ? ` · ${task.start_time}` : ""}
              {task.end_date && task.end_date !== task.start_date && ` → ${task.end_date}`}
              {task.end_time ? ` · ${task.end_time}` : ""}
            </span>
          )}
          {task.due_date && <span className="inline-flex items-center gap-1"><Clock size={12}/> Due {fmtDate(task.due_date)}</span>}
        </div>

        {/* Phase 273 — Additional sessions for non-contiguous schedules */}
        {Array.isArray(task.scheduled_dates) && task.scheduled_dates.filter(s => s.start_date).length > 0 && (
          <div className="mt-3 border border-border rounded-md p-2.5 bg-muted/40" data-testid="my-task-sessions">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">Additional sessions</p>
            <ul className="space-y-1">
              {task.scheduled_dates.filter(s => s.start_date).map((s, idx) => (
                <li key={idx} className="text-xs flex items-center gap-1.5" data-testid={`my-task-session-${idx}`}>
                  <Clock size={11} className="text-muted-foreground shrink-0" />
                  <span>
                    {fmtDate(s.start_date)}{s.start_time ? ` · ${s.start_time}` : ""}
                    {s.end_date && s.end_date !== s.start_date && ` → ${s.end_date}`}
                    {s.end_time ? ` · ${s.end_time}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Inline directions button */}
        {(fullAddress || project?.property_address) && directionsUrl && (
          <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0 flex-1">
              <MapPin size={12} className="shrink-0" />
              <span className="truncate">{fullAddress || project.property_address}</span>
            </div>
            <a
              href={directionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-medium active:scale-95 transition shrink-0"
              data-testid="my-task-directions-btn"
            >
              <NavigationArrow size={12} weight="fill" /> Directions
            </a>
          </div>
        )}
      </div>

      {/* BEFORE photos */}
      <PhotoUploadSection
        title="Before photos"
        category="before"
        photos={beforePhotos}
        endpoint={photosEndpoint}
        mode={mode}
        disabled={isClosed}
        onUploaded={fetchData}
        accent="border-blue-300 dark:border-blue-800"
        testidPrefix="before"
      />

      {/* AFTER photos */}
      <PhotoUploadSection
        title="After photos"
        category="after"
        photos={afterPhotos}
        endpoint={photosEndpoint}
        mode={mode}
        disabled={isClosed}
        onUploaded={fetchData}
        accent="border-emerald-300 dark:border-emerald-800"
        testidPrefix="after"
      />

      {/* Documents */}
      <DocumentUploadSection
        docs={docs}
        endpoint={docsEndpoint}
        mode={mode}
        disabled={isClosed}
        onUploaded={fetchData}
      />

      {/* Notes */}
      <NotesSection
        notes={notes}
        endpoint={noteEndpoint}
        disabled={isClosed}
        onAdded={fetchData}
      />

      {/* Mark complete */}
      {!isClosed && (
        <div className="sticky bottom-0 left-0 right-0 sm:static sm:rounded-xl bg-gradient-to-t from-background via-background to-background/90 sm:bg-card sm:border sm:border-border p-4 -mx-4 -mb-4 sm:mx-0 sm:mb-0 sm:mt-6 pwa-safe-bottom">
          <Button
            onClick={handleComplete}
            disabled={completing || !afterPhotos.length}
            className="w-full"
            data-testid="my-task-complete-btn"
          >
            {completing ? <Spinner size={16} className="animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" weight="fill" />}
            Mark task complete
          </Button>
          {!afterPhotos.length && (
            <p className="text-[10px] text-muted-foreground text-center mt-2">
              Add at least one "After" photo to enable this button.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Photo Upload zone ─────────────────────────── */

function PhotoUploadSection({ title, category, photos, endpoint, mode, disabled, onUploaded, accent, testidPrefix }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const valid = Array.from(fileList).filter(f => f.type.startsWith("image/"));
    if (!valid.length) {
      toast.error("Please pick image files only");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      valid.forEach(f => form.append("files", f));
      form.append("category", category);
      await API.post(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${valid.length} photo(s) uploaded`);
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className={`rounded-xl border-2 border-dashed ${accent} bg-card p-4 mb-4`} data-testid={`${testidPrefix}-photos-section`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Camera size={18} weight="duotone" className="text-primary" />
          <h3 className="text-sm font-semibold font-[Chivo]">{title}</h3>
          <Badge variant="secondary" className="text-[10px]">{photos.length}</Badge>
        </div>
        {!disabled && (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium active:scale-95 transition disabled:opacity-50"
            data-testid={`${testidPrefix}-photos-add-btn`}
          >
            {uploading ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} />}
            {uploading ? "Uploading…" : "Add photos"}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          capture="environment"
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid={`${testidPrefix}-photos-input`}
        />
      </div>

      {photos.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-4" data-testid={`${testidPrefix}-photos-empty`}>
          {disabled ? "No photos uploaded." : "Tap 'Add photos' to take or upload."}
        </p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid={`${testidPrefix}-photos-grid`}>
          {photos.map(p => <PhotoTile key={p.id} photo={p} />)}
        </div>
      )}
    </div>
  );
}

function PhotoTile({ photo }) {
  return (
    <a
      href={photo.file_url}
      target="_blank"
      rel="noopener noreferrer"
      className="relative aspect-square rounded-md overflow-hidden border border-border bg-muted/30 group active:scale-[0.97] transition"
      data-testid={`photo-tile-${photo.id}`}
    >
      <img
        src={photo.file_url}
        alt={photo.file_name || "photo"}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
      />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
        <Eye size={20} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" weight="fill" />
      </div>
    </a>
  );
}

/* ─────────────────────────── Documents ─────────────────────────── */

function DocumentUploadSection({ docs, endpoint, mode, disabled, onUploaded }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "image/gif", "application/pdf"];
    const valid = Array.from(fileList).filter(f => allowed.includes(f.type));
    if (!valid.length) {
      toast.error("Images and PDFs only");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      valid.forEach(f => form.append("files", f));
      await API.post(endpoint, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${valid.length} document(s) uploaded`);
      onUploaded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="documents-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={18} weight="duotone" className="text-primary" />
          <h3 className="text-sm font-semibold font-[Chivo]">Documents</h3>
          <Badge variant="secondary" className="text-[10px]">{docs.length}</Badge>
        </div>
        {!disabled && (
          <button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium active:scale-95 transition disabled:opacity-50"
            data-testid="documents-add-btn"
          >
            {uploading ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} />}
            {uploading ? "Uploading…" : "Add docs"}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/*,application/pdf"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="documents-input"
        />
      </div>

      {docs.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3" data-testid="documents-empty">
          {disabled ? "No documents." : "Receipts, invoices, certificates etc. (PDFs and images)."}
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid="documents-list">
          {docs.map(d => (
            <li key={d.id} className="flex items-center gap-2 px-2.5 py-2 rounded-md border border-border bg-background" data-testid={`document-row-${d.id}`}>
              {d.mime_type?.startsWith("image/") ? (
                <ImageIcon size={14} className="text-primary shrink-0" />
              ) : (
                <FileText size={14} className="text-primary shrink-0" />
              )}
              <a
                href={d.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-xs text-primary hover:underline truncate"
              >
                {d.file_name}
              </a>
              <span className="text-[10px] text-muted-foreground shrink-0">
                {d.size_bytes ? `${Math.round(d.size_bytes / 1024)} KB` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─────────────────────────── Notes ─────────────────────────── */

function NotesSection({ notes, endpoint, disabled, onAdded }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const t = text.trim();
    if (!t) return;
    setSubmitting(true);
    try {
      await API.post(endpoint, { text: t });
      setText("");
      toast.success("Note added");
      onAdded?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 mb-4" data-testid="notes-section">
      <div className="flex items-center gap-2 mb-3">
        <ChatCircle size={18} weight="duotone" className="text-primary" />
        <h3 className="text-sm font-semibold font-[Chivo]">Notes</h3>
        <Badge variant="secondary" className="text-[10px]">{notes.length}</Badge>
      </div>
      {!disabled && (
        <div className="mb-3 space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a quick note (e.g. 'replaced valve, see invoice attached')"
            rows={2}
            maxLength={4000}
            className="text-sm resize-none"
            data-testid="note-textarea"
          />
          <Button onClick={submit} size="sm" disabled={submitting || !text.trim()} data-testid="note-submit-btn">
            {submitting ? <Spinner size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
            Add note
          </Button>
        </div>
      )}
      {notes.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2" data-testid="notes-empty">No notes yet.</p>
      ) : (
        <ul className="space-y-2" data-testid="notes-list">
          {notes.slice().reverse().map(n => (
            <li key={n.id} className="rounded-md border border-border bg-background p-3" data-testid={`note-row-${n.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-[11px] font-semibold">{n.author_name}</span>
                <span className="text-[10px] text-muted-foreground">
                  {n.created_at ? new Date(n.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
                </span>
              </div>
              <p className="text-xs whitespace-pre-wrap leading-relaxed">{n.text}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
